import { formatDay } from '../../format.js';
import { zonedStamp } from '../../inputs/lt-utc.js';
import type { Cell, ReportColumn, ReportTable } from '../reports/reports.js';
import { reportWorkbook } from '../reports/xlsx.js';
import type { CrewMovementRow, MovementTab } from './types.js';

/**
 * Sign On / Off listesinin Excel'i (ADR 0131 Ek 3): ekrandaki sekme, aralık ve süzgeçten geçen
 * satırlar (sayfalamadan bağımsız, hepsi). Var olan rapor çalışma kitabı (`reportWorkbook`):
 * başlık, dönem, oluşturulma zamanı; gün gerçek tarih hücresi, başlık satırı sabit ve süzgeçli.
 * Ayrılış notu dışa aktarılmaz (iç not; veri en aza indirme, KVKK md. 4). İstemcide, internetsiz
 * üretilir; gemide de aynı.
 */

type T = (key: string, options?: Record<string, unknown>) => string;

export function movementsTable(
  rows: readonly CrewMovementRow[],
  tab: MovementTab,
  t: T,
): Pick<ReportTable, 'columns' | 'rows'> {
  const k = (key: string, options?: Record<string, unknown>) => t(`signOnOff.${key}`, options);
  const signOff = tab === 'sign_off';
  const columns: ReportColumn[] = [
    { key: 'name', kind: 'text', label: k('columns.name') },
    { key: 'crewCode', kind: 'text', label: k('export.crewCode') },
    { key: 'rank', kind: 'text', label: k('columns.rank') },
    { key: 'vessel', kind: 'text', label: k('columns.vessel') },
    {
      key: 'date',
      kind: 'date',
      label: k(signOff ? 'columns.signOffDate' : 'columns.joiningDate'),
    },
    { key: 'port', kind: 'text', label: k('columns.port') },
    { key: 'portCode', kind: 'text', label: k('export.portCode') },
    ...(signOff ? [{ key: 'reason', kind: 'text', label: k('columns.reason') } as const] : []),
    { key: 'contractNo', kind: 'text', label: k('export.contractNo') },
    signOff
      ? { key: 'joined', kind: 'date', label: k('export.joiningDate') }
      : { key: 'contractEnd', kind: 'date', label: k('export.contractEnd') },
    { key: 'status', kind: 'status', label: k('columns.status') },
  ];
  const body: Cell[][] = rows.map((row) => [
    row.fullName,
    row.crewCode,
    row.rankChange && row.rank ? k('export.rankChange', { rank: row.rank }) : row.rank,
    row.vesselName,
    row.date,
    row.port,
    row.portCode,
    ...(signOff ? [row.signOffReason ? k(`reason.${row.signOffReason}`) : null] : []),
    row.contractNo,
    signOff ? row.validFrom : row.contractEnd,
    k(`status.${row.status}`),
  ]);
  return { columns, rows: body };
}

export async function movementsWorkbook(
  rows: readonly CrewMovementRow[],
  tab: MovementTab,
  t: T,
  heading: { from: string; to: string; generated: string },
): Promise<Uint8Array> {
  const k = (key: string, options?: Record<string, unknown>) => t(`signOnOff.${key}`, options);
  return reportWorkbook(movementsTable(rows, tab, t), {
    title: k('export.title', { tab: k(`tabs.${tab}`) }),
    period: k('export.period', { from: formatDay(heading.from), to: formatDay(heading.to) }),
    generated: k('export.generated', { at: zonedStamp(heading.generated) }),
  });
}

/** Dosya adı: "sign-on-off-joining-2026-01-01_2026-12-31.xlsx". */
export function movementsFileName(tab: MovementTab, range: { from: string; to: string }): string {
  return `sign-on-off-${tab === 'sign_off' ? 'sign-off' : 'joining'}-${range.from}_${range.to}.xlsx`;
}
